import React, { useState } from 'react';
import { X, Sparkles, DollarSign, AlertTriangle, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import API_BASE_URL from '../config/api';
// Removed js-cookie import - using HttpOnly cookies now

const NetSheetAICoach = ({ isOpen, onClose, inputs, results, dealState }) => {
  const { getToken, isLoaded } = useClerkAuth();
  // analysis holds either a structured object { summary, stats, actions, risks, next_inputs }
  // or null when nothing has been loaded yet. We deliberately do NOT store raw JSON strings.
  const [analysis, setAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // If a field arrives as a JSON-looking string (e.g. '{"summary":"..."}'),
  // recursively dig out the inner summary text so we never render raw JSON.
  const extractCleanSummary = (value, depth = 0) => {
    // Prevent infinite recursion
    if (depth > 5) return typeof value === 'string' ? value : '';
    
    if (value == null) return '';
    if (typeof value === 'object') {
      return extractCleanSummary(value.summary, depth + 1) || '';
    }
    if (typeof value !== 'string') return String(value);
    
    const trimmed = value.trim();
    
    // Check if it looks like JSON
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === 'object') {
          if ('summary' in parsed) {
            return extractCleanSummary(parsed.summary, depth + 1);
          }
          // If it's an object but no summary key, try to find any string value
          const firstStringValue = Object.values(parsed).find(v => typeof v === 'string' && v.length > 50);
          if (firstStringValue) {
            return extractCleanSummary(firstStringValue, depth + 1);
          }
        }
      } catch (_) {
        // JSON parsing failed - check if it's partial/malformed JSON and try to extract text
        // Pattern: looks like {"summary": "actual text here..." - extract the text after first quote
        const partialMatch = trimmed.match(/"summary"\s*:\s*"([^"]+)/);
        if (partialMatch && partialMatch[1]) {
          return partialMatch[1];
        }
      }
    }
    return value;
  };

  // Normalise the backend response into { summary, stats, actions, risks, next_inputs }
  // regardless of whether the model wrapped it in extra JSON, markdown fences, or
  // returned a legacy `coaching_text` field.
  const normaliseAnalysis = (aiResponse) => {
    if (!aiResponse) return null;

    // Legacy plain-text path
    if (typeof aiResponse === 'string') {
      return { summary: extractCleanSummary(aiResponse), stats: {}, actions: [], risks: [], next_inputs: [] };
    }
    if (aiResponse.coaching_text && !aiResponse.summary) {
      return { summary: extractCleanSummary(aiResponse.coaching_text), stats: {}, actions: [], risks: [], next_inputs: [] };
    }

    // Extract summary - handle case where summary might be the entire response stringified
    let summaryText = aiResponse.summary;
    
    // If summary is an object, extract its summary field
    if (summaryText && typeof summaryText === 'object') {
      summaryText = summaryText.summary || JSON.stringify(summaryText);
    }
    
    // Clean the summary text
    summaryText = extractCleanSummary(summaryText);

    return {
      summary: summaryText,
      stats: aiResponse.stats && typeof aiResponse.stats === 'object' ? aiResponse.stats : {},
      actions: Array.isArray(aiResponse.actions) ? aiResponse.actions : [],
      risks: Array.isArray(aiResponse.risks) ? aiResponse.risks : [],
      next_inputs: Array.isArray(aiResponse.next_inputs) ? aiResponse.next_inputs : [],
    };
  };

  // Helper function for API headers (Clerk JWT Bearer auth)
  const getHeaders = (token) => {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const generateAnalysis = async () => {
    if (isAnalyzing) return;
    
    setIsAnalyzing(true);
    
    try {
      // Wait for Clerk to be loaded
      if (!isLoaded) {
        throw new Error('Authentication is still loading. Please wait a moment and try again.');
      }

      // Get fresh Clerk token with retry logic
      let token = null;
      let attempts = 0;
      const maxAttempts = 3;
      
      while (!token && attempts < maxAttempts) {
        try {
          token = await getToken();
          if (!token) {
            attempts++;
            if (attempts < maxAttempts) {
              console.log(`[NetSheetAICoach] Token fetch attempt ${attempts} failed, retrying...`);
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }
        } catch (tokenError) {
          console.error('[NetSheetAICoach] Token fetch error:', tokenError);
          attempts++;
          if (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      }
      
      if (!token) {
        throw new Error('Unable to get authentication token. Please try logging out and back in.');
      }

      // Prepare deal context for AI Coach
      const salePrice = parseFloat(String(inputs.expectedSalePrice || '0').replace(/,/g, ''));
      const commission = parseFloat(inputs.totalCommission || 0);
      const netAmount = parseFloat(results.estimatedSellerNet || 0);
      const netPercentage = salePrice > 0 ? ((netAmount / salePrice) * 100).toFixed(1) : 0;
      
      // Call the AI Coach API
      const response = await fetch(`${API_BASE_URL}/api/ai-coach/generate`, {
        method: 'POST',
        headers: getHeaders(token),
        credentials: 'include',
        body: JSON.stringify({
          context: 'net_sheet_analysis',
          deal_data: {
            sale_price: salePrice,
            commission: commission,
            net_amount: netAmount,
            net_percentage: netPercentage,
            deal_state: dealState,
            inputs: inputs,
            results: results
          },
          year: new Date().getFullYear(),
          stream: false
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication required. Please sign in to access AI Coach.');
        } else if (response.status === 503) {
          throw new Error('AI Coach is currently disabled. Please check back later.');
        } else {
          throw new Error(`AI Coach service error (${response.status}). Please try again.`);
        }
      }

      const aiResponse = await response.json();

      // Normalise to a structured object and render directly from its fields.
      // This prevents raw JSON (e.g. `{ "summary": "..." }`) from leaking into the UI
      // when the model or backend fallback returns a JSON-shaped string.
      const normalised = normaliseAnalysis(aiResponse);

      if (!normalised || !normalised.summary) {
        throw new Error('Invalid AI response format');
      }

      setAnalysis(normalised);

    } catch (error) {
      console.error('Error calling AI Coach API:', error);
      setAnalysis({
        summary: `Unable to generate analysis at the moment. Please try again or check the deal details manually. ${error.message}`,
        stats: {},
        actions: [],
        risks: [],
        next_inputs: [],
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Auto-generate analysis when modal opens if there's sufficient data
  React.useEffect(() => {
    if (isOpen && inputs.expectedSalePrice && !analysis && !isAnalyzing) {
      console.log('Auto-generating analysis for:', inputs.expectedSalePrice);
      generateAnalysis();
    }
  }, [isOpen, inputs.expectedSalePrice, analysis, isAnalyzing]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-t-lg flex-shrink-0">
          <div className="flex items-center">
            <div className="relative">
              <Sparkles className="w-6 h-6 mr-3 text-green-600" />
              <Sparkles className="w-3 h-3 absolute -top-1 -right-1 animate-pulse" />
            </div>
            <div>
              <div className="flex items-center">
                <h2 className="text-xl font-semibold">Fairy AI Coach</h2>
                <span className="ml-2 px-2 py-1 bg-yellow-400 text-purple-900 text-xs font-bold rounded">PRO</span>
              </div>
              <p className="text-purple-100 text-sm">Net Sheet Deal Analysis</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-purple-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
          {/* Deal Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <DollarSign className="w-5 h-5 mr-2 text-green-600" />
                Deal Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Sale Price:</span>
                    <span className="font-semibold">${inputs.expectedSalePrice || 'Not entered'}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Total Commission:</span>
                    <span className="font-semibold">${inputs.totalCommission || 'Not entered'}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Deal State:</span>
                    <Badge variant={dealState ? 'default' : 'secondary'}>
                      {dealState || 'Not specified'}
                    </Badge>
                  </div>
                </div>
                <div>
                  {results.estimatedSellerNet && (
                    <>
                      <div className="flex justify-between py-2">
                        <span className="text-gray-600">Seller Net:</span>
                        <span className="font-bold text-green-600">${results.estimatedSellerNet.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between py-2">
                        <span className="text-gray-600">Total Deductions:</span>
                        <span className="font-semibold text-red-600">${results.totalDeductions?.toLocaleString() || '0'}</span>
                      </div>
                      <div className="flex justify-between py-2">
                        <span className="text-gray-600">Net Percentage:</span>
                        <span className="font-semibold">
                          {results.estimatedSellerNet && inputs.expectedSalePrice 
                            ? `${((parseFloat(results.estimatedSellerNet) / parseFloat(inputs.expectedSalePrice.replace(/,/g, ''))) * 100).toFixed(1)}%`
                            : 'N/A'}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* AI Analysis */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Sparkles className="w-5 h-5 mr-2 text-green-600" />
                AI Analysis
                {isAnalyzing && <Sparkles className="w-4 h-4 ml-2 animate-spin text-purple-500" />}
              </CardTitle>
              <CardDescription>
                AI-powered insights based on your net sheet details and {dealState ? `${dealState} market` : 'market'} conditions
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isAnalyzing ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-center space-y-2">
                    <Sparkles className="w-8 h-8 mx-auto animate-spin text-purple-500" />
                    <p className="text-gray-600">Analyzing your net sheet...</p>
                  </div>
                </div>
              ) : analysis ? (
                <div className="prose prose-sm max-w-none">
                  <div className="text-gray-700 leading-relaxed max-h-64 overflow-y-auto pr-2">
                    <div className="space-y-4">
                      {analysis.summary && (
                        <div>
                          <h4 className="font-semibold text-purple-700 mb-2">Summary</h4>
                          <p className="text-sm whitespace-pre-wrap" data-testid="net-sheet-ai-summary">{analysis.summary}</p>
                        </div>
                      )}
                      {analysis.stats && Object.keys(analysis.stats).length > 0 && (
                        <div>
                          <h4 className="font-semibold text-purple-700 mb-2">Deal Stats</h4>
                          <ul className="text-sm list-disc list-inside space-y-1">
                            {Object.entries(analysis.stats).map(([key, value]) => {
                              const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                              let display;
                              if (value == null) {
                                display = '';
                              } else if (typeof value === 'object') {
                                // Render nested objects as "v1 — v2" joined strings instead of raw JSON
                                display = Object.values(value)
                                  .filter((v) => v != null && typeof v !== 'object')
                                  .map((v) => String(v))
                                  .join(' — ');
                              } else {
                                display = String(value);
                              }
                              return (
                                <li key={key}>
                                  <span className="font-medium">{label}:</span> {display}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                      {analysis.actions && analysis.actions.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-purple-700 mb-2">Recommended Actions</h4>
                          <ul className="text-sm list-disc list-inside space-y-1">
                            {analysis.actions.map((action, idx) => {
                              const text = typeof action === 'object' && action !== null
                                ? (action.action || action.text || action.description || Object.values(action).filter((v) => typeof v !== 'object').join(' — '))
                                : String(action);
                              return <li key={idx}>{text}</li>;
                            })}
                          </ul>
                        </div>
                      )}
                      {analysis.risks && analysis.risks.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-amber-700 mb-2">Potential Risks</h4>
                          <ul className="text-sm list-disc list-inside space-y-1">
                            {analysis.risks.map((risk, idx) => {
                              const text = typeof risk === 'object' && risk !== null
                                ? (risk.risk || risk.text || risk.description || Object.values(risk).filter((v) => typeof v !== 'object').join(' — '))
                                : String(risk);
                              return <li key={idx}>{text}</li>;
                            })}
                          </ul>
                        </div>
                      )}
                      {analysis.next_inputs && analysis.next_inputs.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-purple-700 mb-2">Verify Before Listing</h4>
                          <ul className="text-sm list-disc list-inside space-y-1">
                            {analysis.next_inputs.map((item, idx) => {
                              const text = typeof item === 'object' && item !== null
                                ? (item.input || item.text || item.description || Object.values(item).filter((v) => typeof v !== 'object').join(' — '))
                                : String(item);
                              return <li key={idx}>{text}</li>;
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <AlertTriangle className="w-8 h-8 mx-auto text-amber-500 mb-2" />
                  <p className="text-gray-600 mb-4">Enter deal details to get AI analysis</p>
                  <Button onClick={generateAnalysis} disabled={!inputs.expectedSalePrice}>
                    <Sparkles className="w-4 h-4 mr-2 text-green-600" />
                    Generate Analysis
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

            {/* AI Disclaimer */}
            <div className="text-xs text-gray-500 text-center py-2 px-4 bg-gray-50 rounded-lg border border-gray-200">
              <span className="italic">
                The I Need Numbers AI Fairy Coach can make mistakes. You should verify important information and don't forget it's just a software program.
              </span>
            </div>
          </div>
        </div>

        {/* Fixed Action Buttons at Bottom */}
        <div className="flex-shrink-0 p-6 border-t border-gray-200 bg-white rounded-b-lg">
          <div className="flex justify-between">
            <Button
              onClick={generateAnalysis}
              disabled={isAnalyzing || !inputs.expectedSalePrice}
              variant="outline"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              {isAnalyzing ? 'Analyzing...' : 'Refresh Analysis'}
            </Button>
            <Button
              onClick={onClose}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
            >
              Close Coach
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NetSheetAICoach;